import React, { useState, useCallback, useMemo } from 'react';
import { Panel, FlexboxGrid, Input, InputGroup, Button } from 'rsuite';
import { isMobile } from 'react-device-detect';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

import NavigationBar from '../Shared/NavigationBar';
import RulesDisclaimer from '../Shared/RulesDisclaimer';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';
import {
  RULES_SECTIONS,
  KEYWORD_GLOSSARY,
  LAYERS,
  STATE_BASED_ACTIONS,
  COMBAT_STEPS,
  STACK_AND_PRIORITY,
} from '../../helpers/rulesData';
import useDebouncedSearch from '../../helpers/useDebouncedSearch';

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 40,
  headerMarginBottom: 32,
  titleMarginBottom: 8,
  searchMarginBottom: 24,
  gridGap: 20,
  cardPadding: 20,
  cardTitleMarginBottom: 6,
  cardDescriptionMarginBottom: 12,
  iconSize: 32,
  iconMarginBottom: 8,
  countBadgePadding: '2px 8px',
  countBadgeBorderRadius: 12,
  emptyStatePadding: 40,
  resultItemPadding: 14,
  resultItemBorderRadius: 6,
  resultItemMarginBottom: 10,
  resultBadgePadding: '2px 8px',
  resultBadgeBorderRadius: 4,
  resultBadgeMarginRight: 10,
  resultTitleMarginBottom: 4,
  snippetMarginTop: 6,
  groupHeadingMarginTop: 24,
  groupHeadingMarginBottom: 12,
  resultCountMarginBottom: 16,
  clearButtonMarginLeft: 10,
};

const FONT = {
  title: 32,
  titleMobile: 24,
  subtitle: 16,
  subtitleMobile: 14,
  cardTitle: 18,
  cardDescription: 14,
  countBadge: 14,
  resultTitle: 15,
  resultTitleWeight: 600,
  resultSnippet: 14,
  resultSnippetLineHeight: 1.5,
  resultBadge: 14,
  resultBadgeWeight: 600,
  groupHeading: 16,
  groupHeadingWeight: 600,
  resultCount: 14,
};

const GRID_COLUMNS = {
  desktop: 3,
  mobile: 1,
};

const DEBOUNCE_MS = 300;
const MAX_RESULTS = 20;
const SNIPPET_CONTEXT_LENGTH = 80;

const SOURCE_TYPES = {
  SECTIONS: 'Sections',
  KEYWORDS: 'Keywords',
  LAYERS: 'Layers',
  SBA: 'State-Based Actions',
  COMBAT: 'Combat Steps',
  STACK: 'Stack & Priority',
};

const BADGE_COLORS = {
  [SOURCE_TYPES.SECTIONS]: '#3498db',
  [SOURCE_TYPES.KEYWORDS]: '#27ae60',
  [SOURCE_TYPES.LAYERS]: '#8e44ad',
  [SOURCE_TYPES.SBA]: '#e67e22',
  [SOURCE_TYPES.COMBAT]: '#e74c3c',
  [SOURCE_TYPES.STACK]: '#2980b9',
};

const extractSnippet = (text, term) => {
  const lower = text.toLowerCase();
  const idx = lower.indexOf(term.toLowerCase());
  if (idx === -1) return null;

  const start = Math.max(0, idx - SNIPPET_CONTEXT_LENGTH);
  const end = Math.min(text.length, idx + term.length + SNIPPET_CONTEXT_LENGTH);
  let snippet = text.slice(start, end).replace(/\n+/g, ' ');
  if (start > 0) snippet = '...' + snippet;
  if (end < text.length) snippet = snippet + '...';
  return snippet;
};

const highlightMatch = (snippet, term) => {
  if (!snippet || !term) return snippet;
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = snippet.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <span key={i} style={{ color: '#ffcb6b', backgroundColor: '#3a2e00', fontWeight: 600 }}>
        {part}
      </span>
    ) : (
      part
    )
  );
};

const buildSearchIndex = () => {
  const items = [];

  RULES_SECTIONS.forEach((section) => {
    const searchableText = [section.title, section.description, ...(section.subsections || []).map((s) => s.title + ' ' + s.content)].join(' ');
    items.push({
      source: SOURCE_TYPES.SECTIONS,
      title: `${section.icon} ${section.title}`,
      searchableText,
      link: `/rules/${section.id}`,
    });
  });

  KEYWORD_GLOSSARY.forEach((kw) => {
    const searchableText = [kw.name, kw.reminder, kw.explanation, kw.rulesReference, kw.category].join(' ');
    items.push({
      source: SOURCE_TYPES.KEYWORDS,
      title: kw.name,
      searchableText,
      link: '/rules/keywords',
    });
  });

  LAYERS.forEach((layer) => {
    const exampleText = (layer.examples || []).map((e) => e.scenario + ' ' + e.resolution).join(' ');
    const sublayerText = (layer.sublayers || []).map((s) => s.name + ' ' + s.description).join(' ');
    const searchableText = [layer.name, layer.description, exampleText, sublayerText].join(' ');
    items.push({
      source: SOURCE_TYPES.LAYERS,
      title: `Layer ${layer.number}: ${layer.name}`,
      searchableText,
      link: '/rules/layers',
    });
  });

  STATE_BASED_ACTIONS.forEach((sba) => {
    const exampleText = (sba.examples || []).map((e) => e.scenario + ' ' + e.resolution).join(' ');
    const searchableText = [sba.name, sba.description, sba.rulesReference, exampleText].join(' ');
    items.push({
      source: SOURCE_TYPES.SBA,
      title: sba.name,
      searchableText,
      link: '/rules/state-based-actions',
    });
  });

  COMBAT_STEPS.forEach((step) => {
    const rulesText = (step.keyRules || []).join(' ');
    const exampleText = (step.examples || []).map((e) => e.scenario + ' ' + e.resolution).join(' ');
    const searchableText = [step.name, step.description, rulesText, exampleText].join(' ');
    items.push({
      source: SOURCE_TYPES.COMBAT,
      title: step.name,
      searchableText,
      link: '/rules/combat',
    });
  });

  STACK_AND_PRIORITY.forEach((section) => {
    const exampleText = (section.examples || []).map((e) => e.scenario + ' ' + e.resolution).join(' ');
    const searchableText = [section.title, section.content, exampleText].join(' ');
    items.push({
      source: SOURCE_TYPES.STACK,
      title: section.title,
      searchableText,
      link: '/rules/stack-and-priority',
    });
  });

  return items;
};

const SEARCH_INDEX = buildSearchIndex();

const RulesReference = () => {
  const { preferences } = usePreferences();
  const t = getThemeColors(preferences.theme);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchTerm, setSearchTerm] = useState(initialQuery);

  const handleDebouncedSearch = useCallback((value) => {
    setSearchTerm(value);
    if (value) {
      setSearchParams({ q: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams]);

  const [inputValue, setInputValue] = useDebouncedSearch(
    initialQuery,
    handleDebouncedSearch,
    DEBOUNCE_MS
  );

  const searchResults = useMemo(() => {
    if (!searchTerm) return null;
    const term = searchTerm.toLowerCase();
    const matches = [];
    for (let i = 0; i < SEARCH_INDEX.length && matches.length < MAX_RESULTS; i++) {
      const item = SEARCH_INDEX[i];
      if (item.searchableText.toLowerCase().includes(term)) {
        const snippet = extractSnippet(item.searchableText, searchTerm);
        matches.push({ ...item, snippet });
      }
    }
    return matches;
  }, [searchTerm]);

  const groupedResults = useMemo(() => {
    if (!searchResults) return null;
    const groups = {};
    searchResults.forEach((result) => {
      if (!groups[result.source]) groups[result.source] = [];
      groups[result.source].push(result);
    });
    return groups;
  }, [searchResults]);

  const handleClear = useCallback(() => {
    setInputValue('');
    setSearchTerm('');
    setSearchParams({}, { replace: true });
  }, [setInputValue, setSearchParams]);

  const isSearchActive = searchTerm.length > 0;

  const filteredSections = searchTerm && !searchResults
    ? RULES_SECTIONS
    : RULES_SECTIONS.filter((section) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          section.title.toLowerCase().includes(term) ||
          section.description.toLowerCase().includes(term)
        );
      });

  const columns = isMobile ? GRID_COLUMNS.mobile : GRID_COLUMNS.desktop;
  const columnWidth = Math.floor(24 / columns);

  return (
    <>
      <NavigationBar />
      <div
        style={{
          maxWidth: SPACING.containerMaxWidth,
          margin: '0 auto',
          padding: isMobile
            ? SPACING.containerPaddingMobile
            : SPACING.containerPadding,
        }}
      >
        {/* Header */}
        <div
          style={{
            textAlign: 'center',
            marginTop: SPACING.headerMarginTop,
            marginBottom: SPACING.headerMarginBottom,
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? FONT.titleMobile : FONT.title,
              color: t.textPrimary,
              marginBottom: SPACING.titleMarginBottom,
            }}
          >
            MTG Rules Reference
          </h1>
          <p
            style={{
              fontSize: isMobile ? FONT.subtitleMobile : FONT.subtitle,
              color: t.textMuted,
              margin: 0,
            }}
          >
            A guide to Magic: The Gathering rules and mechanics
          </p>
        </div>

        {/* Search */}
        <div style={{ marginBottom: SPACING.searchMarginBottom, display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <InputGroup inside>
              <Input
                placeholder="Search all rules, keywords, layers, combat, stack..."
                value={inputValue}
                onChange={setInputValue}
              />
              <InputGroup.Addon>🔍</InputGroup.Addon>
            </InputGroup>
          </div>
          {isSearchActive && (
            <Button
              appearance="subtle"
              size="sm"
              onClick={handleClear}
              style={{ marginLeft: SPACING.clearButtonMarginLeft, color: t.accentPrimary }}
            >
              Clear
            </Button>
          )}
        </div>

        {/* Search Results */}
        {isSearchActive && groupedResults ? (
          <div>
            {/* Result count */}
            <div style={{ fontSize: FONT.resultCount, color: t.textFaint, marginBottom: SPACING.resultCountMarginBottom }}>
              {searchResults.length > 0
                ? `Showing ${searchResults.length}${searchResults.length >= MAX_RESULTS ? '+' : ''} result${searchResults.length !== 1 ? 's' : ''} for "${searchTerm}"`
                : null}
            </div>

            {searchResults.length === 0 ? (
              <div style={{ textAlign: 'center', padding: SPACING.emptyStatePadding }}>
                <p style={{ color: t.textFaint, fontSize: FONT.subtitle }}>
                  No results match &ldquo;{searchTerm}&rdquo;. Try a different search term.
                </p>
              </div>
            ) : (
              Object.entries(groupedResults).map(([source, results]) => (
                <div key={source}>
                  <h3 style={{
                    fontSize: FONT.groupHeading,
                    fontWeight: FONT.groupHeadingWeight,
                    color: t.accentPrimary,
                    marginTop: SPACING.groupHeadingMarginTop,
                    marginBottom: SPACING.groupHeadingMarginBottom,
                  }}>
                    {source} ({results.length})
                  </h3>
                  {results.map((result, idx) => (
                    <Link
                      key={`${source}-${idx}`}
                      to={result.link}
                      style={{ textDecoration: 'none', display: 'block' }}
                    >
                      <div
                        style={{
                          backgroundColor: t.cardBackground,
                          border: `1px solid ${t.borderPrimary}`,
                          borderRadius: SPACING.resultItemBorderRadius,
                          padding: SPACING.resultItemPadding,
                          marginBottom: SPACING.resultItemMarginBottom,
                          cursor: 'pointer',
                          transition: 'border-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = t.accentSecondary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = t.borderPrimary;
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACING.resultTitleMarginBottom }}>
                          <span
                            style={{
                              fontSize: FONT.resultBadge,
                              fontWeight: FONT.resultBadgeWeight,
                              color: '#fff',
                              backgroundColor: BADGE_COLORS[source],
                              padding: SPACING.resultBadgePadding,
                              borderRadius: SPACING.resultBadgeBorderRadius,
                              marginRight: SPACING.resultBadgeMarginRight,
                              textTransform: 'uppercase',
                              flexShrink: 0,
                            }}
                          >
                            {source}
                          </span>
                          <span style={{ fontSize: FONT.resultTitle, fontWeight: FONT.resultTitleWeight, color: t.textPrimary }}>
                            {result.title}
                          </span>
                        </div>
                        {result.snippet && (
                          <div style={{ fontSize: FONT.resultSnippet, color: t.textMuted, lineHeight: FONT.resultSnippetLineHeight, marginTop: SPACING.snippetMarginTop }}>
                            {highlightMatch(result.snippet, searchTerm)}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ))
            )}
          </div>
        ) : (
          /* Section Grid (default view) */
          filteredSections.length > 0 ? (
            <FlexboxGrid
              style={{ gap: SPACING.gridGap }}
              justify="start"
            >
              {filteredSections.map((section) => (
                <FlexboxGrid.Item
                  key={section.id}
                  colspan={columnWidth}
                  style={{
                    flex: `0 0 calc(${100 / columns}% - ${SPACING.gridGap * (columns - 1) / columns}px)`,
                    maxWidth: `calc(${100 / columns}% - ${SPACING.gridGap * (columns - 1) / columns}px)`,
                  }}
                >
                  <Panel
                    bordered
                    style={{
                      backgroundColor: t.cardBackground,
                      borderColor: t.borderPrimary,
                      cursor: 'pointer',
                      padding: SPACING.cardPadding,
                      height: '100%',
                      transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = t.accentSecondary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = t.borderPrimary;
                    }}
                    onClick={() => navigate(`/rules/${section.id}`)}
                  >
                    <div
                      style={{
                        fontSize: SPACING.iconSize,
                        marginBottom: SPACING.iconMarginBottom,
                      }}
                    >
                      {section.icon}
                    </div>
                    <h4
                      style={{
                        fontSize: FONT.cardTitle,
                        color: t.textPrimary,
                        marginBottom: SPACING.cardTitleMarginBottom,
                      }}
                    >
                      {section.title}
                    </h4>
                    <p
                      style={{
                        fontSize: FONT.cardDescription,
                        color: t.textMuted,
                        marginBottom: SPACING.cardDescriptionMarginBottom,
                      }}
                    >
                      {section.description}
                    </p>
                    <span
                      style={{
                        fontSize: FONT.countBadge,
                        color: t.accentPrimary,
                        backgroundColor: t.borderPrimary,
                        padding: SPACING.countBadgePadding,
                        borderRadius: SPACING.countBadgeBorderRadius,
                      }}
                    >
                      {section.subsections?.length || 0}{' '}
                      {(section.subsections?.length || 0) === 1 ? 'topic' : 'topics'}
                    </span>
                  </Panel>
                </FlexboxGrid.Item>
              ))}
            </FlexboxGrid>
          ) : (
            <div style={{ textAlign: 'center', padding: SPACING.emptyStatePadding }}>
              <p style={{ color: t.textFaint, fontSize: FONT.subtitle }}>
                No sections match your search.
              </p>
            </div>
          )
        )}
        <RulesDisclaimer />
      </div>
    </>
  );
};

export default RulesReference;
